import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LOG_EVENT } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { v4 as uuidv4 } from "uuid";
import sessionStore from "../store/sessionStore";
import { usePostHog } from "posthog-js/react";

const EVENT_TYPES = {
  LOGGED_IN: "logged-in",
  LADDER_COMPLETION: "ladder-completion",
  CLIMB_COMPLETION: "climb-completion",
  // CERTIFICATE_COMPLETION: "certificate-completion", // this one is heandled in the pipeline resolver.
  HEARD_OF_HAT: "heard-of-hat",
  SAVED_HAT: "saved-hat",
  APPLICATION_ERROR: "application-error",
  EARNED_XP: "earned-xp",
  ACTIVITY_COMPLETION: "activity-completion",
  CONTENT_COMPLETION: "content-completion",
  CLUSTER_CONTENT_COMPLETED: "cluster-content-completed",
  RESOURCE_DOWNLOAD: "resource-download",
  HAT_EXPLORED: "hat-explored",
  FACET_CHANGED: "facet-changed",
  WATCHED_VIDEO: "watched-video",
  LISTENED_TO_AUDIO: "listened-to-audio",
  PROFILE_ONBOARD_COMPLETED: "profile-onboard-completed",
  HAT_RATED: "hat-rated",
  ADD_BUILDING_BLOCK: "add-building-block",
  PATHWAY_RATED: "pathway-rated",
  RECOMMENDED_CLUSTER_FOUND: "recommended-cluster-found",
  ONBOARD_SOMETHING_ELSE: "onboard-something-else",
  CLIMBER_RESET: "climber-reset",
  CAREER_PLAN_DRAFT_CREATED: "career-plan-draft-created",
  CAREER_PLAN_SUBMITTED: "career-plan-submitted",
  COURSE_PLAN_SUBMITTED: "course-plan-submitted",
};

// What events should we be sending to posthog?
const POSTHOG_WHITE_LIST = [
  "hat-explored",
  "profile-onboard-completed",
  "cluster-content-completed",
  "career-plan-draft-created",
];

export const useReporting = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  /*
    recentEvents: This is a bit annoying.
    When we log an event, it takes a bit of time to go through the pipeline and get saved to dynamo.
    While that's happening, we might try to getNextSteps, which needs to know about that event.
    
    So we'll store the event in a local array and when we request next steps, we'll pass all our recent events along. 
    The get next steps resolver will use our events if they're not already in dynamo. It can check if they're there
    by checking the uuid.
  */

  const logEvent = async ({
    whatId,
    eventType,
    maxScore = null,
    score = null,
    result = null,
    metadata = {},
  }) => {
    const uuid = uuidv4();
    const input = {
      whatId,
      eventType,
      maxScore,
      score,
      result,
      metadata: JSON.stringify({
        ...metadata,
        uuid,
      }),
    };

    // Store the event in the format expected by the next steps processor
    const recentEvent = {
      id: `recent-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      eventType,
      data: JSON.stringify({
        ...metadata,
        result,
        score: maxScore ? score : undefined,
        maxScore: maxScore || undefined,
        whatId,
        uuid, // UUID goes in the data for deduplication checking
      }),
      timestamp: new Date().toISOString(),
    };

    sessionStore.addRecentEvent(recentEvent);

    if (POSTHOG_WHITE_LIST.includes(eventType)) {
      posthog.capture(eventType, {
        whatId,
        eventType,
        maxScore,
        score,
        result,
        uuid,
        ...metadata,
      });
    }

    return graphQLClient.request(LOG_EVENT, {
      input,
    });
  };

  const mutation = useMutation({
    mutationFn: logEvent,
    retry: true,
    onError: (error) => {
      console.error("Error logging event:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nextSteps"] });
    },
  });

  const reportClimberReset = async () => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.CLIMBER_RESET,
    });
  };

  /**
   * User logged into the application
   */
  const reportLogin = async () => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.LOGGED_IN,
    });
  };

  /**
   * Completed a career ladder
   * @param {string} ladderId - ID of the completed ladder
   * @param {number} score - Score achieved on the ladder
   * @param {number} maxScore - Maximum possible score for the ladder
   * @param {string} result - Result of the ladder completion
   * @param {number} time - Time taken to complete the ladder
   
   * learnositySessionId - the session id from learnosity
   * reportingFields - map of key:value pairs for fields with ReportMe tags on them
   */
  const reportLadderCompletion = async (
    ladderId,
    score,
    maxScore,
    result = "success",
    time = 0,
    learnositySessionId = null,
    reportingFields = {},
  ) => {
    return mutation.mutateAsync({
      whatId: "LADDER-" + ladderId,
      eventType: EVENT_TYPES.LADDER_COMPLETION,
      score,
      maxScore,
      result,
      metadata: { time, ladderId, learnositySessionId, ...reportingFields },
    });
  };

  /**
   * Completed a career climb
   * @param {string} climbId - ID of the completed climb
   * @param {boolean} wasAssigned - Whether the climb was assigned to the user
   */
  const reportClimbCompletion = async (climbId, wasAssigned) => {
    return mutation.mutateAsync({
      whatId: "CLIMB-" + climbId,
      eventType: EVENT_TYPES.CLIMB_COMPLETION,
      metadata: { wasAssigned, climbId },
    });
  };

  /**
   * User indicated awareness of a career hat
   * @param {string} hatId - ID of the career hat
   * @param {boolean} heardOf - Whether the user has heard of this hat
   * @param {string} greatFor - RIASEC letters indicating what this hat is great for
   * @param {string} themes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportHeardOfHat = async (
    hatId,
    heardOf,
    greatFor = null,
    themes = null,
    clusters = null,
    workValues = null,
  ) => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.HEARD_OF_HAT,
      result: heardOf ? "yes" : "no",
      metadata: { greatFor, themes, hatId, clusters, workValues },
    });
  };

  /**
   * User saved a career hat
   * @param {string} hatId - ID of the career hat
   * @param {boolean} saved - Whether the hat was saved or unsaved
   * @param {string} greatFor - RIASEC letters indicating what this hat is great for
   * @param {string} themes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportSavedHat = async (
    hatId,
    saved,
    greatFor = null,
    themes = null,
    clusters = null,
    workValues = null,
  ) => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.SAVED_HAT,
      result: saved ? "yes" : "no",
      metadata: { greatFor, themes, hatId, clusters, workValues },
    });
  };

  /**
   * Log application errors
   * @param {object} errorDetails - Details about the error that occurred
   */
  const reportError = async (errorDetails) => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.APPLICATION_ERROR,
      metadata: errorDetails,
    });
  };

  /**
   * User earned experience points
   * @param {number} xp - Amount of XP earned
   * @param {number} totalXp - Total XP the user now has
   * @param {string} reason - Reason for earning the XP
   */
  const reportXPEarned = async (xp, totalXp, reason) => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.EARNED_XP,
      score: xp,
      maxScore: totalXp,
      metadata: { reason },
    });
  };

  /**
   * Completed an activity
   * @param {string} activityId - ID of the completed activity
   * @param {string} result - Result of the activity completion
   * @param {number} time - Time taken to complete the activity
   */
  const reportActivityCompletion = async (activityId, result, time) => {
    return mutation.mutateAsync({
      whatId: "ACTIVITY-" + activityId,
      eventType: EVENT_TYPES.ACTIVITY_COMPLETION,
      result,
      metadata: { time, activityId },
    });
  };

  /**
   * Explored a career hat by completing all the perks/quirks
   * @param {string} hatId - ID of the explored hat
   * @param {string} greatFor - RIASEC letters indicating what this hat is great for
   * @param {string} themes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportHatExplored = async (
    hatId,
    greatFor,
    themes = null,
    clusters = null,
    firstTime = false,
  ) => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.HAT_EXPLORED,
      metadata: {
        greatFor,
        themes,
        hatId,
        clusters,
        firstTime: firstTime ? "true" : "false",
      },
    });
  };

  /**
   * Completed content consumption
   * @param {string} contentId - ID of the content
   * @param {string} blockType - Type of content block
   * @param {string} contentType - Type of content
   * @param {string} contentLevel - Level of the content
   * @param {string} parentId - ID of the parent content
   */
  const recordContentComplete = async (
    contentId,
    blockType,
    contentType,
    contentLevel,
    parentId,
  ) => {
    return mutation.mutateAsync({
      whatId: "CONTENT-" + contentId,
      eventType: EVENT_TYPES.CONTENT_COMPLETION,
      result: "success",
      metadata: {
        blockType,
        contentType,
        contentLevel,
        parentId,
        contentId,
      },
    });
  };

  /**
   * Completed all content within a cluster
   * @param {string} clusterId - ID of the cluster
   */
  const reportClusterContentCompleted = async (clusterId) => {
    return mutation.mutateAsync({
      whatId: "CLUSTER-" + clusterId,
      eventType: EVENT_TYPES.CLUSTER_CONTENT_COMPLETED,
      result: "success",
      metadata: {
        clusterId,
      },
    });
  };

  /**
   * A recommended cluster has been found for the climber
   * @param {string} clusterId - ID of the recommended cluster
   */
  const reportRecommendedClusterFound = async (clusterId) => {
    return mutation.mutateAsync({
      whatId: "CLUSTER-" + clusterId,
      eventType: EVENT_TYPES.RECOMMENDED_CLUSTER_FOUND,
      result: "success",
      metadata: { clusterId },
    });
  };

  /**
   * Downloaded a resource
   * @param {string} resourceId - ID of the downloaded resource
   * @param {string} resourceName - Name of the resource
   * @param {string} resourceType - Type of the resource
   * @param {string} category - Category of the resource
   * @param {number} filesize - Size of the file in bytes
   * @param {string} mimeType - MIME type of the file
   */
  const reportResourceDownload = async (
    resourceId,
    resourceName,
    resourceType,
    category,
    filesize,
    mimeType,
  ) => {
    return mutation.mutateAsync({
      whatId: "RESOURCE-" + resourceId,
      eventType: EVENT_TYPES.RESOURCE_DOWNLOAD,
      result: "success",
      metadata: {
        resourceName,
        resourceType,
        category,
        filesize,
        mimeType,
        resourceId,
      },
    });
  };

  /**
   * Occurs when a climber's facet changes for any reason
   * @param {string} facetType - Type of facet that changed
   * @param {number} currentScore - Current score of the facet
   * @param {number} maxScore - Maximum possible score for the facet
   * percentage is auto-calculated from currentScore and maxScore
   */
  const reportFacetChanged = async (facetType, currentScore, maxScore) => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.FACET_CHANGED,
      score: currentScore,
      maxScore: maxScore,
      metadata: {
        facetType,
        percentage: Math.round((currentScore / maxScore) * 100),
      },
    });
  };

  /**
   * Watched the given video
   * @param {string} videoId - ID of the video that was watched
   */
  const reportWatchedVideo = async (videoId) => {
    return mutation.mutateAsync({
      whatId: "VIDEO-" + videoId,
      eventType: EVENT_TYPES.WATCHED_VIDEO,
      result: "success",
      metadata: { videoId },
    });
  };

  const reportListenedToAudio = async (audioId) => {
    return mutation.mutateAsync({
      whatId: "AUDIO-" + audioId,
      eventType: EVENT_TYPES.LISTENED_TO_AUDIO,
      result: "success",
      metadata: { audioId },
    });
  };

  /**
   * The profile onboarding has been completed
   * @param {string} topClusters - CSV of cluster IDs in format 0001,0002,0003
   * @param {string} topThemes - CSV of theme IDs in format 0001,0002,0003
   * @param {string} careerKnowledge - Career knowledge level (KNOWN/UNKNOWN)
   */
  const reportProfileOnboardCompleted = async (
    careerStage,
    careerConfidence,
    topThemes,
  ) => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.PROFILE_ONBOARD_COMPLETED,
      result: "success",
      metadata: { careerStage, careerConfidence, topThemes },
    });
  };

  /**
   * The climber has used the like/dislike/not for me rating widget
   * @param {string} rating - The rating given (like/dislike/not for me)
   * @param {string} hatId - ID of the hat being rated
   * @param {string} greatFor - RIASEC letters, use contains operator (e.g., "RI")
   * @param {string} themes - CSV of theme IDs in format 0001,0002,0003
   * @param {boolean} firstTime - Whether this is the first time rating this hat
   */
  const reportHatRated = async (
    rating,
    hatId,
    greatFor,
    themes,
    firstTime,
    clusters,
    pathways,
    workValues,
  ) => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.HAT_RATED,
      result: rating,
      metadata: {
        greatFor,
        themes,
        firstTime,
        hatId,
        clusters,
        pathways,
        workValues,
      },
    });
  };

  /**
   * Happens when the climber creates a new building block
   * @param {string} blockName - Name of the building block created
   * @param {string} blockCategory - Category of the building block
   */
  const reportAddBuildingBlock = async (
    blockName,
    blockCategory,
    firstTime = true,
  ) => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.ADD_BUILDING_BLOCK,
      result: "success",
      metadata: { blockName, blockCategory, firstTime },
    });
  };

  /**
   * The climber has rated a pathway
   * @param {string} pathwayId - ID of the pathway being rated
   * @param {string} clusterId - ID of the cluster the pathway belongs to
   * @param {boolean} firstTime - Whether this is the first time rating this pathway
   * @param {string} result - The rating result
   */
  const reportPathwayRated = async (
    pathwayId,
    clusterId,
    firstTime,
    result,
  ) => {
    return mutation.mutateAsync({
      whatId: "PATHWAY-" + pathwayId,
      eventType: EVENT_TYPES.PATHWAY_RATED,
      result,
      metadata: {
        pathwayId,
        clusterId,
        firstTime: firstTime ? "true" : "false",
      },
    });
  };

  /**
   * Onboard something else has been completed
   * @param {string} careerArea - The career area selected
   */
  const reportOnboardSomethingElse = async (careerArea) => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.ONBOARD_SOMETHING_ELSE,
      result: "success",
      metadata: { careerArea },
    });
  };

  /**
   * Career plan draft has been created
   * @param {string} careerPlanId - ID of the career plan
   * @param {string} pathwayId - ID of the pathway
   * @param {string} hatId - ID of the selected hat
   * @param {string} pathwayName - Name of the pathway
   * @param {string} hatName - Name of the selected hat
   */
  const reportCareerPlanDraftCreated = async (careerPlanId, pathwayId) => {
    return mutation.mutateAsync({
      whatId: careerPlanId,
      eventType: EVENT_TYPES.CAREER_PLAN_DRAFT_CREATED,
      result: "success",
      metadata: {
        careerPlanId,
        pathwayId,
      },
    });
  };

  /**
   * Career plan has been submitted for review
   * @param {string} careerPlanId - ID of the career plan
   * @param {string} pathwayId - ID of the pathway
   * @param {string} hatId - ID of the selected hat
   * @param {string} pathwayName - Name of the pathway
   * @param {string} hatName - Name of the selected hat
   * @param {object} statusInfo - Status change information
   */
  const reportCareerPlanSubmitted = async (
    careerPlanId,
    pathwayId,
    hatId,
    pathwayName,
    hatName,
    statusInfo = {},
  ) => {
    return mutation.mutateAsync({
      whatId: careerPlanId,
      eventType: EVENT_TYPES.CAREER_PLAN_SUBMITTED,
      result: "SUBMITTED",
      metadata: {
        careerPlanId,
        pathwayId,
        hatId,
        pathwayName,
        hatName,
        ...statusInfo,
      },
    });
  };

  /**
   * Course plan has been submitted for review
   * @param {string} coursePlanId - ID of the course plan
   * @param {string} pathwayId - ID of the pathway
   * @param {object} planMetadata - Additional plan metadata
   */
  const reportCoursePlanSubmitted = async (
    coursePlanId,
    pathwayId,
    planMetadata = {},
  ) => {
    return mutation.mutateAsync({
      whatId: coursePlanId,
      eventType: EVENT_TYPES.COURSE_PLAN_SUBMITTED,
      result: "SUBMITTED",
      metadata: {
        coursePlanId,
        pathwayId,
        ...planMetadata,
      },
    });
  };

  return {
    reportLogin,
    reportLadderCompletion,
    reportClimbCompletion,
    reportHatExplored,
    reportHeardOfHat,
    reportListenedToAudio,
    reportSavedHat,
    reportError,
    reportXPEarned,
    reportActivityCompletion,
    recordContentComplete,
    reportClusterContentCompleted,
    reportRecommendedClusterFound,
    reportResourceDownload,
    reportFacetChanged,
    reportWatchedVideo,
    reportProfileOnboardCompleted,
    reportHatRated,
    reportAddBuildingBlock,
    reportPathwayRated,
    reportOnboardSomethingElse,
    reportClimberReset,
    reportCareerPlanDraftCreated,
    reportCareerPlanSubmitted,
    reportCoursePlanSubmitted,
    logEvent: mutation,
    recentEvents: sessionStore.recentEvents,
    isLoading: mutation.isLoading,
    error: mutation.error,
  };
};
